import { Box } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { FaFileArrowDown } from 'react-icons/fa6';
import { ChevronDownIcon } from '@/assets/icons';
import type { ActionMenuItem } from '@/components/Atoms/FaceliftActionMenu';
import Show from '@/components/Molecules/Show';
import type { DataTableExportFilterMenuProps } from '@/components/Organisms/DataTable/DataTable.types';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { MenuPrimitives as PMMenu } from '@/types/MenuPrimitives.types';

const DataTableExportFilterMenu = ({
	trigger,
	csv,
	excel,
	pdf,
	exportAllAsCSV,
	exportAllAsExcel,
	exportAllAsPDF,
	exportCurrentAsCSV,
	exportCurrentAsExcel,
	exportCurrentAsPDF,
}: DataTableExportFilterMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const exportMenuItems: ActionMenuItem[] = [
		{
			title: 'Current',
			submenu: [
				...(csv && exportCurrentAsCSV
					? [
						{
							title: 'CSV',
							action: exportCurrentAsCSV,
						},
					]
					: []),
				...(excel && exportCurrentAsExcel
					? [
						{
							title: 'Excel',
							action: exportCurrentAsExcel,
						},
					]
					: []),
				...(pdf && exportCurrentAsPDF
					? [
						{
							title: 'PDF',
							action: exportCurrentAsPDF,
						},
					]
					: []),
			],
		},
		{
			title: 'All',
			submenu: [
				...(csv && exportAllAsCSV
					? [
						{
							title: 'CSV',
							action: exportAllAsCSV,
						},
					]
					: []),
				...(excel && exportAllAsExcel
					? [
						{
							title: 'Excel',
							action: exportAllAsExcel,
						},
					]
					: []),
				...(pdf && exportAllAsPDF
					? [
						{
							title: 'PDF',
							action: exportAllAsPDF,
						},
					]
					: []),
			],
		},
	];

	const [expandedItem, setExpandedItem] = useState<string | null>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const menuBg = useColorModeValue('white', 'gray.800');
	const textColor = useColorModeValue('gray.800', 'gray.100');

	const menuItems: ActionMenuItem[] = exportMenuItems;

	const handleItemClick = (item: ActionMenuItem) => {
		if (item.submenu) {
			setExpandedItem(expandedItem === item.title ? null : item.title);
		} else {
			setIsOpen(false);
			setExpandedItem(null);
		}
	};

	const handleSubmenuItemClick = (item: ActionMenuItem) => {
		item.action?.();
		setIsOpen(false);
		setExpandedItem(null);
	};

	return (
		<Box ref={triggerRef} cursor="pointer">
			<PMMenu.Root
				open={isOpen}
				onOpenChange={(d: { open: boolean }) => setIsOpen(d.open)}
			>
				<PMMenu.Trigger
					_focus={{ boxShadow: 'none', outline: 'none' }}
					_focusVisible={{ boxShadow: 'none', outline: 'none' }}
					border="none"
				>
					{trigger}
				</PMMenu.Trigger>
				<PMMenu.Positioner>
					<PMMenu.Content
						bg={menuBg}
						borderRadius="xl"
						boxShadow="xl"
						py="2.5"
						width="55"
						minHeight="auto"
						maxHeight="96"
						zIndex="popover"
						color={textColor}
						display="flex"
						flexDirection="column"
						gap="1.5"
						transition="height 0.3s ease-in-out"
						overflow="hidden"
					>
						{menuItems.map((item) => (
							<Box key={item.title}>
								<Box
									px="3"
									py="2"
									display="flex"
									alignItems="center"
									justifyContent="space-between"
									cursor="pointer"
									_hover={{
										bg: 'brand.solidGradient',
										color: 'white',
										'& > div > div': { color: 'white' },
										'& svg': { color: 'white' },
									}}
									onClick={() => handleItemClick(item)}
									transition="all 0.2s"
									borderRadius="md"
								>
									<Box display="flex" alignItems="center" gap="3">
										<Box fontSize="sm" fontWeight="500" color={textColor}>
											{item.title}
										</Box>
									</Box>

									<Show when={!!item.submenu}>
										<Box
											display="flex"
											alignItems="center"
											transform={
												expandedItem === item.title
													? 'rotate(180deg)'
													: 'rotate(0deg)'
											}
											transition="transform 0.3s ease-in-out"
										>
											<ChevronDownIcon width={12} height={12} />
										</Box>
									</Show>
								</Box>

								<Show when={!!item.submenu}>
									<Box
										overflow="hidden"
										transition="all 0.3s ease-in-out"
										maxHeight={expandedItem === item.title ? '48' : '0'}
										opacity={expandedItem === item.title ? 1 : 0}
									>
										{item.submenu?.map((subItem) => (
											<Box
												key={subItem.title}
												px="3"
												py="1.5"
												display="flex"
												alignItems="center"
												gap={2}
												cursor="pointer"
												_hover={{
													bg: 'brand.solidGradient',
													color: 'white',
													'& > div': { color: 'white' },
													'& svg': { color: 'white' },
												}}
												onClick={() => handleSubmenuItemClick(subItem)}
												transition="all 0.2s"
												borderRadius="md"
												ml="2"
											>
												<Box fontSize="sm" fontWeight="400" color={textColor}>
													<FaFileArrowDown color={textColor} />
												</Box>
												<Box fontSize="sm" fontWeight="400" color={textColor}>
													{subItem.title}
												</Box>
											</Box>
										))}
									</Box>
								</Show>
							</Box>
						))}
					</PMMenu.Content>
				</PMMenu.Positioner>
			</PMMenu.Root>
		</Box>
	);
};

export default DataTableExportFilterMenu;
